import {
  useEffect,
  useState,
  useCallback,
  useLayoutEffect,
  useRef,
} from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  Tooltip,
  Image,
  SimpleGrid,
  Spacer,
  Tag,
  Icon,
  Link,
  useColorModeValue,
} from '@chakra-ui/react';
import type { TextProps } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import CompanyAvatar from 'assets/img/avatars/company-avatar.png';
import { normalizeGeographies } from 'constants/icp';
import { InvitePartnerModal } from './InvitePartnerModal';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import { HiArrowTrendingUp } from 'react-icons/hi2';
import {
  buildStyles,
  CircularProgressbarWithChildren,
} from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';

/** Optional ICP payload from partner search API */
export type PartnerIcp = {
  targetIndustries?: string[];
  targetCompanySizes?: string[];
  targetGeographies?: string[];
};

/** Shape matches `/api/partners/search` rows after mapping in `Partners.tsx`. */
export type PartnerSearchResultRow = {
  id: string;
  companyName: string;
  hubspotEmail: string;
  hubspotPortalId: number | string;
  hubspotName?: string;
  createdAt?: string;
  industry?: string;
  icp?: PartnerIcp | null;
  products?: string;
  targetMarket?: string;
  website?: string;
  logoUrl: string | null;
  receiverAccountIds: string[];
  notifyTeamMembersOnInvite: boolean;
  connectionStatus: string;
  partnerId: string;
  /** 0–100 from partner recommendations API; omitted for plain directory search rows */
  fitScore?: number;
  /** Recommendations API: directory vs web-sourced suggestion */
  source?: 'in_network' | 'out_of_network';
};

/** Strip markdown-style source links and related citation noise from API strings */
function stripEnrichmentCitations(text: string): string {
  if (!text?.trim()) return '';
  let t = text.trim();
  const wrapped = /\s*\(\[[^\]]+\]\([^)]+\)\)/g;
  const markdownLink = /\s*\[[^\]]+\]\([^)]+\)/g;
  let prev = '';
  while (t !== prev) {
    prev = t;
    t = t.replace(wrapped, '').replace(markdownLink, '');
  }
  return t
    .replace(/\(\s*\)/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Subtitle under company name — company size only (industry is in tags near the footer). */
function partnerSubtitle(partner: PartnerSearchResultRow): string {
  const size = partner.icp?.targetCompanySizes?.[0];
  if (size?.trim()) return size.trim();
  return '';
}

function partnerSummary(partner: PartnerSearchResultRow): string {
  const raw = partner.targetMarket?.trim() || partner.products?.trim() || '';
  return stripEnrichmentCitations(raw);
}

function primarySegmentTag(partner: PartnerSearchResultRow): string {
  const fromIcp = partner.icp?.targetIndustries?.[0]?.trim();
  if (fromIcp) return stripEnrichmentCitations(fromIcp);
  return stripEnrichmentCitations(partner.industry?.trim() ?? '');
}

function PartnerSourceTag({
  source,
}: {
  source: NonNullable<PartnerSearchResultRow['source']>;
}) {
  const isInNetwork = source === 'in_network';
  const bg = useColorModeValue(
    isInNetwork ? 'blue.50' : 'orange.50',
    isInNetwork ? 'rgba(66, 153, 225, 0.18)' : 'rgba(237, 137, 54, 0.15)',
  );
  const color = useColorModeValue(
    isInNetwork ? 'blue.700' : 'orange.800',
    isInNetwork ? 'blue.200' : 'orange.200',
  );
  const label = isInNetwork ? 'In network' : 'Out of network';

  return (
    <Tag
      size="sm"
      borderRadius="full"
      bg={bg}
      color={color}
      borderWidth={0}
      px={2.5}
      py={0.5}
      fontWeight="600"
    >
      <Text fontSize="xs">{label}</Text>
    </Tag>
  );
}

function PartnerFitScoreRing({ score }: { score: number }) {
  const clamped = Math.min(100, Math.max(0, Number.isFinite(score) ? score : 0));
  const trailColor = useColorModeValue('#E9EDF7', '#1B254B');
  const pathColor = useColorModeValue('#01B574', '#48BB78');

  return (
    <Box
      w="40px"
      h="40px"
      flexShrink={0}
      aria-label={`Fit score ${Math.round(clamped)} out of 100`}
      role="img"
    >
      <CircularProgressbarWithChildren
        value={clamped}
        maxValue={100}
        styles={buildStyles({
          pathColor,
          trailColor,
          strokeLinecap: 'round',
          pathTransitionDuration: 0.45,
          textSize: '0px',
          textColor: 'transparent',
        })}
      >
        <Text
          fontSize="13px"
          fontWeight="800"
          color="navy.900"
          lineHeight="1"
        >
          {Math.round(clamped)}
        </Text>
      </CircularProgressbarWithChildren>
    </Box>
  );
}

const PartnerCardLogo = ({
  logoUrl,
  companyName,
}: {
  logoUrl?: string | null;
  companyName?: string;
}) => (
  <Flex
    w="44px"
    h="44px"
    align="center"
    justify="center"
    bg="white"
    borderRadius="lg"
    boxShadow="sm"
    overflow="hidden"
    flexShrink={0}
  >
    <Image
      src={logoUrl ?? undefined}
      alt={companyName || 'Partner'}
      w="36px"
      h="36px"
      objectFit="contain"
      fallbackSrc={CompanyAvatar}
    />
  </Flex>
);

/** Description with line clamp; full text in tooltip only when visually truncated. */
function PartnerCardDescription({
  text,
  noOfLines = 5,
  ...textProps
}: { text: string; noOfLines?: number } & Omit<
  TextProps,
  'children' | 'noOfLines'
>) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [truncated, setTruncated] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setTruncated(el.scrollHeight > el.clientHeight + 1);
  }, []);

  useLayoutEffect(() => {
    measure();
  }, [text, noOfLines, measure]);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  return (
    <Tooltip
      label={text}
      isDisabled={!truncated}
      placement="top"
      openDelay={400}
      maxW="300px"
      hasArrow
    >
      <Text ref={ref} noOfLines={noOfLines} {...textProps}>
        {text}
      </Text>
    </Tooltip>
  );
}

function PartnerCardShell({ children }: { children: React.ReactNode }) {
  const cardBackground = useColorModeValue(
    'linear-gradient(to bottom, #FFFFFF 0%, #FFFFFF 22%, color-mix(in srgb, #EDFFF8 51%, #EDFFF8 30%) 100%)',
    'linear-gradient(to bottom, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.06) 22%, rgba(0, 196, 117, 0.04) 100%)',
  );
  const cardBorder = useColorModeValue('secondaryGray.200', 'whiteAlpha.200');

  return (
    <Box
      borderRadius="12px"
      borderWidth="1px"
      borderColor={cardBorder}
      backgroundImage={cardBackground}
      p={{ base: 5, md: 6 }}
      maxW="100%"
      display="flex"
      flexDirection="column"
      h="full"
      minH="full"
    >
      {children}
    </Box>
  );
}

const skeletonShimmer = keyframes`
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
`;

function SkeletonBone({
  h = '12px',
  w = '100%',
  borderRadius = 'full',
  ...rest
}: React.ComponentProps<typeof Box>) {
  const base = useColorModeValue('#EDF2F7', 'rgba(255,255,255,0.08)');
  const highlight = useColorModeValue('#F7FAFC', 'rgba(255,255,255,0.18)');

  return (
    <Box
      h={h}
      w={w}
      borderRadius={borderRadius}
      backgroundImage={`linear-gradient(90deg, ${base} 0%, ${highlight} 45%, ${base} 90%)`}
      backgroundSize="200% 100%"
      animation={`${skeletonShimmer} 1.4s ease-in-out infinite`}
      {...rest}
    />
  );
}

function PartnerResultCardSkeleton() {
  const cardBorder = useColorModeValue('secondaryGray.200', 'whiteAlpha.200');

  return (
    <Box
      borderRadius="12px"
      borderWidth="1px"
      borderColor={cardBorder}
      bg="white"
      p={{ base: 5, md: 6 }}
      display="flex"
      flexDirection="column"
      h="full"
      minH="280px"
    >
      <Flex align="flex-start" gap={3} mb={5} justify="space-between">
        <Flex align="center" gap={3} minW={0} flex={1}>
          <SkeletonBone
            h="44px"
            w="44px"
            borderRadius="lg"
            flexShrink={0}
          />
          <SkeletonBone h="16px" w="55%" />
        </Flex>
        <SkeletonBone h="40px" w="40px" borderRadius="full" flexShrink={0} />
      </Flex>

      <Flex direction="column" gap={2.5} mb={5}>
        <SkeletonBone h="10px" w="100%" />
        <SkeletonBone h="10px" w="96%" />
        <SkeletonBone h="10px" w="88%" />
        <SkeletonBone h="10px" w="72%" />
        <SkeletonBone h="10px" w="58%" />
      </Flex>

      <SkeletonBone h="14px" w="42%" mb={5} />

      <Spacer />

      <SkeletonBone h="28px" w="46%" borderRadius="full" mb={4} />
      <SkeletonBone h="44px" w="100%" borderRadius="full" />
    </Box>
  );
}

function RecommendedPartnersLoadingSkeleton({
  showSectionCopy = true,
}: {
  showSectionCopy?: boolean;
}) {
  return (
    <Box mt={showSectionCopy ? 6 : 4} aria-busy="true" aria-live="polite">
      {showSectionCopy ? (
        <Box mb={5}>
          <SkeletonBone h="18px" w="160px" mb={4} />
          <Flex direction="column" gap={2.5}>
            <SkeletonBone h="12px" w="100%" />
            <SkeletonBone h="12px" w="97%" />
            <SkeletonBone h="12px" w="68%" />
          </Flex>
        </Box>
      ) : null}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} w="100%">
        <PartnerResultCardSkeleton />
        <PartnerResultCardSkeleton />
        <PartnerResultCardSkeleton />
      </SimpleGrid>
    </Box>
  );
}

function PartnerInviteFallbackCard({
  searchQuery,
  hasConnectedPortal,
  hasNoRemainingPartners,
  onOpenInviteModal,
}: {
  searchQuery: string;
  hasConnectedPortal: boolean;
  hasNoRemainingPartners: boolean;
  onOpenInviteModal: () => void;
}) {
  const canAct = hasConnectedPortal && !hasNoRemainingPartners;
  const disabledReason = !hasConnectedPortal
    ? 'Connect your HubSpot portal to invite partners'
    : hasNoRemainingPartners
      ? 'You have reached your partner limit. Upgrade your plan to invite more partners.'
      : '';

  return (
    <PartnerCardShell>
      <Flex align="flex-start" gap={3}>
        <PartnerCardLogo />
        <Box minW={0}>
          <Text fontSize="lg" fontWeight="bold" color="navy.900" noOfLines={2}>
            {searchQuery}
          </Text>
          <Text fontSize="sm" color="secondaryGray.600">
            No partner returned for this exact company name. Send an email
            invite instead.
          </Text>
        </Box>
      </Flex>
      <Spacer />
      <Box flexShrink={0}>
        <Tooltip label={disabledReason} isDisabled={canAct}>
          <Button
            w="100%"
            h="44px"
            borderRadius="full"
            bg="navy.900"
            color="white"
            fontWeight="600"
            fontSize="sm"
            leftIcon={<Text as="span">+</Text>}
            _hover={{ bg: canAct ? 'navy.800' : 'navy.900' }}
            _active={{ bg: canAct ? 'navy.700' : 'navy.900' }}
            onClick={() => canAct && onOpenInviteModal()}
            isDisabled={!canAct}
            opacity={canAct ? 1 : 0.55}
          >
            Invite
          </Button>
        </Tooltip>
      </Box>
    </PartnerCardShell>
  );
}

function PartnerCardFooter(props: {
  partnerId: string;
  connectionStatus: string;
  onInvitePartner: (partnerId: string, receiverAccountIds?: string[]) => void;
  receiverAccountIds?: string[];
  hasConnectedPortal: boolean;
  hasNoRemainingPartners: boolean;
  /** Out-of-network recommendations: email invite modal instead of Connect */
  preferEmailInvite?: boolean;
  onOpenEmailInvite?: () => void;
}) {
  const {
    partnerId,
    connectionStatus,
    onInvitePartner,
    receiverAccountIds,
    hasConnectedPortal,
    hasNoRemainingPartners,
    preferEmailInvite,
    onOpenEmailInvite,
  } = props;

  const missingDirectoryPartnerId = !partnerId?.trim();

  const inviteDisabledReason = !hasConnectedPortal
    ? 'Connect your HubSpot portal to invite partners'
    : hasNoRemainingPartners
      ? 'You have reached your partner limit. Upgrade your plan to invite more partners.'
      : '';

  const connectDisabledReason = !hasConnectedPortal
    ? 'Connect your HubSpot portal to request partner connections'
    : hasNoRemainingPartners
      ? 'You have reached your partner limit. Upgrade your plan to request more partner connections.'
      : missingDirectoryPartnerId
        ? 'This partner is not in your directory yet — invite them by email instead.'
        : '';

  const useInviteFlow = Boolean(preferEmailInvite && onOpenEmailInvite);

  const canInviteByEmail =
    hasConnectedPortal && !hasNoRemainingPartners;

  const canConnect =
    hasConnectedPortal &&
    !hasNoRemainingPartners &&
    !missingDirectoryPartnerId;

  if (connectionStatus === 'connected') {
    return (
      <Box flexShrink={0} w="100%">
        <Flex
          w="100%"
          h="44px"
          align="center"
          justify="center"
          borderRadius="full"
          bg="green.50"
          borderWidth="1px"
          borderColor="green.200"
        >
          <Text fontSize="sm" fontWeight="700" color="green.600">
            Connected
          </Text>
        </Flex>
      </Box>
    );
  }

  if (connectionStatus === 'pending') {
    return (
      <Box flexShrink={0} w="100%">
        <Flex
          w="100%"
          h="44px"
          align="center"
          justify="center"
          borderRadius="full"
          bg="secondaryGray.100"
        >
          <Text fontSize="sm" color="secondaryGray.600">
            Pending
          </Text>
        </Flex>
      </Box>
    );
  }

  if (useInviteFlow) {
    return (
      <Box flexShrink={0} w="100%">
        <Tooltip label={inviteDisabledReason} isDisabled={canInviteByEmail}>
          <Button
            w="100%"
            h="44px"
            borderRadius="full"
            bg="navy.900"
            color="white"
            fontWeight="600"
            fontSize="sm"
            leftIcon={<Text as="span">+</Text>}
            _hover={{ bg: canInviteByEmail ? 'navy.800' : 'navy.900' }}
            _active={{ bg: canInviteByEmail ? 'navy.700' : 'navy.900' }}
            onClick={() =>
              canInviteByEmail && onOpenEmailInvite && onOpenEmailInvite()
            }
            isDisabled={!canInviteByEmail}
            opacity={canInviteByEmail ? 1 : 0.55}
          >
            Invite
          </Button>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box flexShrink={0} w="100%">
      <Tooltip label={connectDisabledReason} isDisabled={canConnect}>
        <Button
          w="100%"
          h="44px"
          borderRadius="full"
          bg="navy.900"
          color="white"
          fontWeight="600"
          fontSize="sm"
          leftIcon={<Text as="span">+</Text>}
          _hover={{ bg: canConnect ? 'navy.800' : 'navy.900' }}
          _active={{ bg: canConnect ? 'navy.700' : 'navy.900' }}
          onClick={() =>
            canConnect &&
            onInvitePartner(
              partnerId,
              receiverAccountIds?.length ? receiverAccountIds : undefined,
            )
          }
          isDisabled={!canConnect}
          opacity={canConnect ? 1 : 0.55}
        >
          Connect
        </Button>
      </Tooltip>
    </Box>
  );
}

function PartnerResultCard({
  partner,
  onInvitePartner,
  onOpenInviteModal,
  hasConnectedPortal,
  hasNoRemainingPartners,
}: {
  partner: PartnerSearchResultRow;
  onInvitePartner: (partnerId: string, receiverAccountIds?: string[]) => void;
  onOpenInviteModal: () => void;
  hasConnectedPortal: boolean;
  hasNoRemainingPartners: boolean;
}) {
  const subtitle = partnerSubtitle(partner);
  const summary = partnerSummary(partner);
  const segment = primarySegmentTag(partner);
  const geo =
    normalizeGeographies(partner.icp?.targetGeographies ?? [])[0] ?? '';

  const fit = partner.fitScore;
  const showFitScore = fit != null && Number.isFinite(fit);

  return (
    <PartnerCardShell>
      <Flex align="flex-start" gap={3} mb={4} justify="space-between">
        <Flex align="flex-start" gap={3} minW={0}>
          <PartnerCardLogo
            logoUrl={partner.logoUrl}
            companyName={partner.companyName}
          />
          <Box minW={0}>
            <Text fontSize="lg" fontWeight="bold" color="navy.900" noOfLines={2}>
              {partner.companyName}
            </Text>
            {subtitle ? (
              <Text fontSize="sm" color="secondaryGray.600" noOfLines={2}>
                {subtitle}
              </Text>
            ) : null}
          </Box>
        </Flex>
        {showFitScore ? <PartnerFitScoreRing score={fit} /> : null}
      </Flex>

      {summary ? (
        <PartnerCardDescription
          text={summary}
          noOfLines={5}
          fontSize="sm"
          color="secondaryGray.700"
          lineHeight="tall"
          mb={4}
        />
      ) : null}

      {partner.website?.trim() || partner.source ? (
        <Flex direction="column" align="flex-start" gap={2} mb={4}>
          {partner.website?.trim() ? (
            <Link
              href={partner.website.trim()}
              isExternal
              fontSize="xs"
              fontWeight="600"
              color="brand.600"
              display="block"
              noOfLines={1}
            >
              {partner.website.trim()}
            </Link>
          ) : null}
          {partner.source ? (
            <PartnerSourceTag source={partner.source} />
          ) : null}
        </Flex>
      ) : null}

      <Spacer />

      {segment || geo ? (
        <Flex
          direction="column"
          align="flex-start"
          gap={2}
          mb={4}
          flexShrink={0}
        >
          {segment ? (
            <Tag
              size="md"
              borderRadius="full"
              bg="purple.50"
              color="purple.700"
              borderWidth={0}
              px={3}
              py={1}
            >
              <Flex align="center" gap={1.5}>
                <Icon as={HiArrowTrendingUp} boxSize={3.5} />
                <Text fontSize="xs" fontWeight="600">
                  {segment}
                </Text>
              </Flex>
            </Tag>
          ) : null}
          {geo ? (
            <Tag
              size="md"
              borderRadius="full"
              bg="secondaryGray.100"
              color="navy.900"
              borderWidth={0}
              px={3}
              py={1}
            >
              <Text fontSize="xs" fontWeight="500">
                {geo}
              </Text>
            </Tag>
          ) : null}
        </Flex>
      ) : null}

      <PartnerCardFooter
        partnerId={partner.partnerId}
        connectionStatus={partner.connectionStatus}
        onInvitePartner={onInvitePartner}
        receiverAccountIds={partner.receiverAccountIds}
        hasConnectedPortal={hasConnectedPortal}
        hasNoRemainingPartners={hasNoRemainingPartners}
        preferEmailInvite={partner.source === 'out_of_network'}
        onOpenEmailInvite={onOpenInviteModal}
      />
    </PartnerCardShell>
  );
}

function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line -- debounce: omit onChange/debounce from deps; parent passes memoized onSearchPartners (Partners.tsx useCallback).
  }, [value]);

  return (
    <SearchBar
      {...props}
      value={value}
      onChange={(e: any) => setValue(e.target.value)}
      h="44px"
      borderRadius="16px"
    />
  );
}

export const PartnerResults = ({
  partnerResults,
  partnersLoading,
  recommendedPartners = [],
  recommendationsLoading = false,
  recommendationsSummary = '',
  recommendationsError = null,
  onSearchPartners,
  onSendEmailInvite,
  onInvitePartner,
  hasConnectedPortal = true,
  activePartners = 0,
  partnerLimit = 0,
}: {
  partnerResults: PartnerSearchResultRow[];
  partnersLoading: boolean;
  recommendedPartners?: PartnerSearchResultRow[];
  recommendationsLoading?: boolean;
  recommendationsSummary?: string;
  recommendationsError?: string | null;
  onSearchPartners: (value: string) => void;
  onSendEmailInvite: ({ to }: { to: string }) => void;
  loading: boolean;
  onInvitePartner: (partnerId: string, receiverAccountIds?: string[]) => void;
  hasConnectedPortal?: boolean;
  activePartners?: number;
  partnerLimit?: number;
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [inviteModalOpen, setInviteModalOpen] = useState(false);

  const hasNoRemainingPartners = activePartners >= partnerLimit;

  const searchQueryTrimmed = searchQuery.trim();
  const isSearchMode = searchQueryTrimmed.length > 0;
  const searchReady = searchQueryTrimmed.length >= 3;

  const hasNoMatchingPartners = useCallback(() => {
    if (!searchReady) return false;
    return (
      partnerResults.every((partner) => partner.companyName !== searchQuery) &&
      !partnersLoading
    );
  }, [searchQuery, searchReady, partnerResults, partnersLoading]);

  const displayedLoading = isSearchMode
    ? partnersLoading
    : recommendationsLoading;

  const showRecommendedGrid =
    !isSearchMode &&
    !recommendationsLoading &&
    recommendedPartners.length > 0 &&
    !recommendationsError;

  const showSearchGrid =
    isSearchMode &&
    searchReady &&
    !partnersLoading &&
    (hasNoMatchingPartners() || partnerResults.length > 0);

  const showResultsGrid = showRecommendedGrid || showSearchGrid;

  const resultsHeading = isSearchMode ? 'Results' : 'Recommended for you';

  const cleanedRecommendationsSummary = stripEnrichmentCitations(
    recommendationsSummary ?? '',
  );

  return (
    <Box>
      <DebouncedInput
        value={searchQuery}
        onChange={(value) => {
          setSearchQuery(String(value));
          onSearchPartners(String(value));
        }}
        className="p-2 font-lg shadow border border-block"
        placeholder="Search..."
      />
      {(isSearchMode || !displayedLoading) ? (
        <>
          <Text fontSize="18px" fontWeight="bold" mb={2} mt={6}>
            {resultsHeading}
          </Text>
          {!isSearchMode && cleanedRecommendationsSummary ? (
            <Text fontSize="sm" color="secondaryGray.600" mb={4}>
              {cleanedRecommendationsSummary}
            </Text>
          ) : null}
        </>
      ) : null}
      {!isSearchMode && recommendationsError ? (
        <Text fontSize="sm" color="red.500" mb={4} mt={6}>
          {recommendationsError}
        </Text>
      ) : null}
      {displayedLoading ? (
        <RecommendedPartnersLoadingSkeleton showSectionCopy={!isSearchMode} />
      ) : null}
      {isSearchMode && searchQueryTrimmed.length > 0 && searchQueryTrimmed.length < 3 ? (
        <Flex direction="column" align="start" mt={6}>
          <Text color="secondaryGray.500" fontWeight="400" mb={3}>
            Type at least 3 characters to search the partner directory.
          </Text>
        </Flex>
      ) : null}
      {!isSearchMode &&
        !recommendationsLoading &&
        !recommendationsError &&
        recommendedPartners.length === 0 && (
          <Flex direction="column" align="start" mt={6}>
            <Text color="secondaryGray.500" fontWeight="400" mb={3}>
              No recommendations are available yet. Try searching above for a
              company name.
            </Text>
          </Flex>
        )}
      {showResultsGrid && (
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} w="100%">
          {showSearchGrid && hasNoMatchingPartners() && (
            <PartnerInviteFallbackCard
              searchQuery={searchQuery}
              hasConnectedPortal={hasConnectedPortal ?? true}
              hasNoRemainingPartners={hasNoRemainingPartners}
              onOpenInviteModal={() => setInviteModalOpen(true)}
            />
          )}
          {(isSearchMode ? partnerResults : recommendedPartners).map(
            (partner) => (
              <PartnerResultCard
                key={partner.id}
                partner={partner}
                onInvitePartner={(pid, ids) => onInvitePartner(pid, ids)}
                onOpenInviteModal={() => setInviteModalOpen(true)}
                hasConnectedPortal={hasConnectedPortal}
                hasNoRemainingPartners={hasNoRemainingPartners}
              />
            ),
          )}
        </SimpleGrid>
      )}

      <InvitePartnerModal
        isOpen={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        onSendInvite={onSendEmailInvite}
      />
    </Box>
  );
};
